// Four-card summary strip above the contract timeline. Reads the
// summarised milestone state and renders MetricCards (reused from the
// deliverable tab) for Contract value, Value completed, Next milestone,
// and Slipped. When the tab is scoped to a single SOW the value tile's
// subtitle becomes the milestone-total vs SOW-value reconciliation
// caption (see sowAllocation.ts).
//
// Locked semantics worth re-stating:
// - F2 denominator: "X of N milestones" reflects the FULL milestone
//   count; governance ($0) milestones contribute 0 to value but DO
//   appear in both numerator (when completed) and denominator. The
//   `summariseMilestones` helper enforces this.
// - U2 visible Info icon on Value completed (NOT hover-only). Clicking
//   the icon opens a popover with the locked disclaimer copy, so the
//   tooltip is reachable by keyboard + screen reader and survives mobile
//   (no hover events). Per po-ictcm.
// - F6 currency tooltip: every dollar surface carries a full-precision
//   tooltip with the GST-status-not-recorded disclaimer.
// - F5 Slipped tooltip: hover/focus the card to see all slipped
//   milestones with their daysLate.
// - Slipped card hidden from DOM when count is zero (NOT just visually
//   hidden — must not appear at all).

import { useState } from "react";
import { Info } from "lucide-react";
import MetricCard from "../deliverable/MetricCard";
import ProgressBar from "../../../common/ProgressBar";
import { getBrisbaneToday } from "../../../../utils/getBrisbaneToday";
import { gstSuffix } from "../../../../utils/gstSuffix";
import {
  formatCurrencyAbbreviated,
  summariseMilestones,
} from "./summarise";
import SlipTag from "./SlipTag";
import {
  formatAllocationAmount,
  type SowAllocationSummary,
} from "./sowAllocation";
import type { Milestone } from "../../../../types";

/** Caption for the SOW value tile: the milestone-total vs SOW-value
 *  reconciliation. The GST basis is inherited from the tile's headline
 *  suffix immediately above (same proximity rule as the SOW detail
 *  current-value annotation). */
function SowAllocationCaption({
  allocation,
}: {
  allocation: SowAllocationSummary;
}) {
  const total = formatAllocationAmount(allocation.milestonesTotal);
  if (allocation.state === "reconciled") {
    return (
      <span
        data-testid="sow-allocation-caption"
        className="text-emerald-600 dark:text-emerald-400"
      >
        Milestones total {total} · fully allocated ✓
      </span>
    );
  }
  if (allocation.state === "over") {
    return (
      <span
        data-testid="sow-allocation-caption"
        className="text-amber-600 dark:text-amber-400"
      >
        Milestones total {total} ·{" "}
        {formatAllocationAmount(allocation.difference)} over SOW value
      </span>
    );
  }
  return (
    <span data-testid="sow-allocation-caption">
      Milestones total {total} ·{" "}
      {formatAllocationAmount(allocation.difference)} unallocated
    </span>
  );
}

export interface MilestoneSummaryStripProps {
  milestones: readonly Milestone[];
  contractValue: number | undefined;
  /** Stored GST intent on the parent contract. Drives the suffix on
   *  Contract value / Value completed labels. Undefined keeps the
   *  legacy "GST status not recorded" placeholder. */
  gstApplies?: boolean;
  /** Headline label shown on the leftmost tile. The tab passes a
   *  scope-aware label here ("Total contract value" / "Contract value" /
   *  "SOW value") so the tile reads as the slice the user has selected.
   *  Defaults to "Contract value" to preserve the strip's behaviour for
   *  callers that don't yet pass a scope. */
  valueLabel?: string;
  /** Milestone-total vs SOW-value reconciliation, shown as the value
   *  tile's subtitle when the tab is scoped to a single SOW. Absent on
   *  All / Contract scope — reconciling mixed-scope milestone totals
   *  against a single value is not meaningful there. */
  sowAllocation?: SowAllocationSummary;
  today?: string;
}

const VALUE_ACCEPTED_DISCLAIMER =
  "Total value of milestones accepted by the agency. Does not reflect invoices submitted, approved, or paid.";

export default function MilestoneSummaryStrip({
  milestones,
  contractValue,
  gstApplies,
  valueLabel = "Contract value",
  sowAllocation,
  today,
}: MilestoneSummaryStripProps) {
  const [infoOpen, setInfoOpen] = useState(false);
  if (milestones.length === 0) return null;

  const todayDay = today ?? getBrisbaneToday();
  const summary = summariseMilestones(milestones, contractValue, todayDay);
  const suffix = gstSuffix(gstApplies);

  const contractValueAbbr = formatCurrencyAbbreviated(
    summary.contractValueTotal,
    gstApplies,
  );
  const valueAcceptedAbbr = formatCurrencyAbbreviated(
    summary.valueAccepted,
    gstApplies,
  );
  const nextPaymentAbbr = summary.next
    ? formatCurrencyAbbreviated(
        summary.next.milestone.paymentAmount ?? 0,
        gstApplies,
      )
    : undefined;

  const slippedTooltip = summary.slipped
    .map((s) => `${s.milestone.name}: ${s.daysLate}d late`)
    .join("\n");

  return (
    <section
      data-testid="milestone-summary-strip"
      aria-label="Milestone summary"
      className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4"
    >
      <MetricCard
        testId="summary-contract-value"
        label={`${valueLabel} (${suffix})`}
        value={
          <span title={contractValueAbbr.long}>{contractValueAbbr.short}</span>
        }
        subtitle={
          sowAllocation ? (
            <SowAllocationCaption allocation={sowAllocation} />
          ) : (
            `across ${summary.milestoneCount} milestones`
          )
        }
      />

      <div className="rounded-md border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800/60">
        <div className="flex items-center gap-1.5">
          <span className="text-caption font-medium text-slate-500 dark:text-slate-400">
            Value accepted ({suffix})
          </span>
          <button
            type="button"
            onClick={() => setInfoOpen((v) => !v)}
            aria-label="Value accepted — info"
            aria-expanded={infoOpen}
            className="inline-flex h-4 w-4 items-center justify-center rounded-md text-slate-500 hover:text-slate-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-400 dark:hover:text-slate-200"
          >
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
        <div
          data-testid="summary-value-accepted-value"
          className="mt-1 flex items-baseline gap-2 text-h2 font-semibold text-emerald-600 dark:text-emerald-400"
        >
          <span title={valueAcceptedAbbr.long}>{valueAcceptedAbbr.short}</span>
          <span
            data-testid="summary-value-accepted-inline-pct"
            className="text-caption font-medium text-slate-500 dark:text-slate-400"
          >
            · {summary.valueAcceptedPct}%
          </span>
        </div>
        <div className="mt-2">
          <ProgressBar
            percentage={summary.valueAcceptedPct}
            tone="success"
            label="Value accepted against contract"
          />
        </div>
        <div className="mt-1 text-caption text-slate-500 dark:text-slate-400">
          {summary.acceptedCount} of {summary.milestoneCount} milestones
        </div>
        {infoOpen && (
          <p className="mt-2 rounded-md bg-slate-50 p-2 text-caption text-slate-700 dark:bg-slate-900 dark:text-slate-200">
            {VALUE_ACCEPTED_DISCLAIMER}
          </p>
        )}
      </div>

      {summary.next && nextPaymentAbbr ? (
        <MetricCard
          testId="summary-next-milestone"
          label={`Next milestone (${suffix})`}
          value={
            <span title={nextPaymentAbbr.long}>{nextPaymentAbbr.short}</span>
          }
          subtitle={`${summary.next.milestone.name} · ${summary.next.daysUntil === 0 ? "today" : `in ${summary.next.daysUntil}d`}`}
        />
      ) : (
        <MetricCard
          testId="summary-next-milestone"
          label="Next milestone"
          value={
            <span className="text-body text-slate-500 dark:text-slate-400">
              No upcoming milestones
            </span>
          }
        />
      )}

      {summary.slipped.length > 0 && summary.worst && (
        <div
          data-testid="summary-slipped"
          className="rounded-md border border-red-200 bg-linear-to-b from-red-500/5 to-transparent p-4 dark:border-red-900/60"
        >
          <div className="text-caption font-medium text-slate-500 dark:text-slate-400">
            Slipped
          </div>
          <div
            data-testid="summary-slipped-value"
            className="mt-1 text-h2 font-semibold text-red-600 dark:text-red-400"
          >
            <span title={slippedTooltip}>{summary.slipped.length}</span>
          </div>
          <div className="mt-2">
            <SlipTag
              variant="late"
              days={summary.worst.daysLate}
              headline
            />
            <span className="ml-2 text-caption text-slate-500 dark:text-slate-400">
              worst slip
            </span>
          </div>
        </div>
      )}
    </section>
  );
}
